export const DEFAULT_CURRENCY = "INR" as const;

const CURRENCY_FORMATTERS = new Map<string, Intl.NumberFormat>();
const SYMBOL_FORMATTERS = new Map<string, Intl.NumberFormat>();

export function currencySymbol(code: string | null | undefined): string {
  const normalized = (code || DEFAULT_CURRENCY).toUpperCase();
  const cacheKey = `symbol:${normalized}`;
  let formatter = SYMBOL_FORMATTERS.get(cacheKey);
  try {
    if (!formatter) {
      formatter = new Intl.NumberFormat(localeFor(normalized), {
        style: "currency",
        currency: normalized,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      SYMBOL_FORMATTERS.set(cacheKey, formatter);
    }
    const symbol = formatter
      .formatToParts(0)
      .find((part) => part.type === "currency")?.value;
    return symbol ?? `${normalized} `;
  } catch {
    return `${normalized} `;
  }
}

function localeFor(currency: string | null | undefined): string {
  if (!currency) return "en-IN";
  return currency.toUpperCase() === "INR" ? "en-IN" : "en-US";
}

/** ISO currency minor-unit precision; payment support is gated separately. */
export function currencyMinorUnitDigits(
  currency: string = DEFAULT_CURRENCY,
): number {
  try {
    return (
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
      }).resolvedOptions().maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

export function formatPrice(
  minor: number | null | undefined,
  currency: string | null | undefined = DEFAULT_CURRENCY,
): string {
  const value = typeof minor === "number" ? minor : Number(minor ?? 0);
  const normalized = (currency || DEFAULT_CURRENCY).toUpperCase();
  const digits = currencyMinorUnitDigits(normalized);
  const major = Number.isFinite(value) ? value / 10 ** digits : 0;
  const cacheKey = `standard:${normalized}`;
  let formatter = CURRENCY_FORMATTERS.get(cacheKey);
  try {
    if (!formatter) {
      formatter = new Intl.NumberFormat(localeFor(normalized), {
        style: "currency",
        currency: normalized,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
      CURRENCY_FORMATTERS.set(cacheKey, formatter);
    }
    return formatter.format(major);
  } catch {
    return `${normalized} ${major.toFixed(digits)}`;
  }
}

export function formatPriceCompact(
  minor: number | null | undefined,
  currency: string | null | undefined = DEFAULT_CURRENCY,
): string {
  const value = typeof minor === "number" ? minor : Number(minor ?? 0);
  if (!Number.isFinite(value)) return formatPrice(0, currency);
  const normalized = (currency || DEFAULT_CURRENCY).toUpperCase();
  const digits = currencyMinorUnitDigits(normalized);
  const major = value / 10 ** digits;
  const isWhole = Number.isInteger(major);
  try {
    return new Intl.NumberFormat(localeFor(normalized), {
      style: "currency",
      currency: normalized,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: isWhole ? 0 : digits,
      maximumFractionDigits: digits,
    }).format(major);
  } catch {
    return `${normalized} ${major.toFixed(isWhole ? 0 : digits)}`;
  }
}

export function parseMajorToMinor(
  input: string | number,
  currency: string = DEFAULT_CURRENCY,
): number | null {
  const n = typeof input === "number" ? input : Number(String(input).trim());
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 10 ** currencyMinorUnitDigits(currency));
}
